import React, { useEffect } from 'react';
import { Modal, Form, Typography, Alert, message } from 'antd';
import type { Lead, LeadSource, Contact } from '../../types';
import LeadPropertyFormSection from './LeadPropertyFormSection';
import LocationFormSection from '../common/LocationFormSection';
import ContactsFormSection from '../common/ContactsFormSection';
import { citiesByCountry } from '../../constants/countries';
import { CONTACT_TYPE_OPTIONS } from '../../constants/leadOptions';
import { addLead, updateLead } from '../../api/leads';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

interface LeadFormValues {
  name: string;
  status: 'New' | 'Disqualified';
  source: LeadSource;
  address: string;
  city: string;
  pincode?: string;
  country: string;
  description?: string;
  notes?: string;
  contacts: {
    name: string;
    position?: string;
    email: string;
    phone: string;
    type: Contact['type'];
  }[];
}

interface LeadModalProps {
  open: boolean;
  lead?: Lead | null;
  onSuccess?: () => void;
  onCancel: () => void;
}

const LeadModal: React.FC<LeadModalProps> = ({ open, lead, onSuccess, onCancel }) => {
  const [form] = Form.useForm<LeadFormValues>();
  const { t } = useTranslation(['common', 'sales']);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (lead) {
      form.setFieldsValue({
        name: lead.name,
        status: lead.status,
        source: lead.source,
        address: lead.address,
        city: lead.city,
        pincode: lead.pincode,
        country: lead.country,
        description: lead.description,
        notes: lead.notes,
        contacts: lead.contacts.map(contact => ({
          name: contact.name,
          position: contact.position,
          email: contact.email,
          phone: contact.phone,
          type: contact.type,
        })),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: 'New', contacts: [{ type: 'Primary' }] });
    }
  }, [lead, form]);

  const handleSave = async () => {
    try {
      setError(null);
      const values = await form.validateFields();
      
      if (!values.address) {
        setError('Address is required.');
        return;
      }

      setLoading(true);

      const now = new Date();
      const leadData = {
        name: values.name,
        status: values.status,
        source: values.source,
        address: values.address,
        city: values.city,
        pincode: values.pincode ?? '',
        country: values.country,
        description: values.description ?? '',
        notes: values.notes ?? '',
        contacts: values.contacts.map(contact => ({
          name: contact.name,
          position: contact.position ?? '',
          email: contact.email,
          phone: contact.phone,
          type: contact.type,
          value: contact.email, // Use email as the value
        })),
        updated_at: now,
      };

      if (lead) {
        await updateLead(lead.id, leadData);
        message.success(t('sales:success.updateSuccess', { entity: t('sales:leads.lead') }));
      } else {
        await addLead({
          ...leadData,
          created_at: now,
          activities: [],
        });
        message.success(t('sales:success.createSuccess', { entity: t('sales:leads.lead') }));
      }

      onSuccess?.();
      onCancel();
    } catch (errorInfo: any) {
      let errorMsg = t('sales:errors.saveFailed', { entity: t('sales:leads.lead') });
      if (errorInfo?.code && errorInfo?.message) {
        errorMsg += `: [${errorInfo.code}] ${errorInfo.message}`;
      } else if (errorInfo?.errorFields?.length > 0) {
        errorMsg += ': ' + errorInfo.errorFields.map((f: any) => f.errors.join(', ')).join(' ');
      } else if (errorInfo?.message) {
        errorMsg += ': ' + errorInfo.message;
      } else if (typeof errorInfo === 'object') {
        errorMsg += ': ' + JSON.stringify(errorInfo);
      } else if (typeof errorInfo === 'string') {
        errorMsg += ': ' + errorInfo;
      }
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<Title level={4}>{lead ? t('sales:leads.edit') : t('sales:leads.add')}</Title>}
      open={open}
      onOk={handleSave}
      onCancel={onCancel}
      width={800}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form<LeadFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ contacts: [{ type: 'Primary' }] }}
      >
        <Title level={5} style={{ marginBottom: 24 }}>{t('sales:properties.details')}</Title>
        <LeadPropertyFormSection form={form} t={t} />

        <Title level={5} style={{ marginTop: 32, marginBottom: 24 }}>{t('common:location')}</Title>
        <LocationFormSection t={t} citiesByCountry={citiesByCountry} />

        <Title level={5} style={{ marginTop: 32, marginBottom: 24 }}>{t('common:contacts')}</Title>
        <ContactsFormSection t={t} contactTypeOptions={CONTACT_TYPE_OPTIONS} />

        {error && (
          <Alert 
            type="error" 
            message={error} 
            showIcon 
            style={{ marginTop: 24, marginBottom: 0 }} 
          />
        )}
      </Form>
    </Modal>
  );
};

export default LeadModal; 